import isEqual from 'lodash/isEqual';
import throttle from 'lodash/throttle';
import pako from 'pako';
import EventEmitter from 'eventemitter3';
import * as Sentry from '@sentry/vue';
import { SENTRY_ENABLE } from './const';
import { NORMAL_SUBSCRIBE_MAP, WORKER_MSG_TOPIC, WsData } from '~/common/js/workerConfig';
import { debounceMessage } from '~/frontend-shared/helper/util';
import { getCurHost } from '~/frontend-shared/helper/dom';
import { useTradeStore } from '~/store/trade';

const getWsUrl = () => {
  let wsUrl: string
  if (process.client) {
    const $curHost = getCurHost(window.location.origin) ?? {}
    wsUrl = $curHost.wsUrl
  }
  return wsUrl;
};
class WsSubject extends EventEmitter {
  static WS_DELAY_THRESHOLD = 5000;
  url;
  ws: WebSocket | undefined;
  retryTimes;
  latestSymbol: number;
  subjectMap: Map<string, object[]>;
  dataStore: Map<string, WsData | WsData[]>;
  priceDataStore: Map<number, WsData>;
  realtimeSymbolStore: Map<number, WsData>;
  klineDataStore: Array<WsData>;
  isApplicationActive: boolean;
  heartBeat: NodeJS.Timeout | undefined;
  connectTimer: NodeJS.Timeout | undefined;
  scheduleTimer: NodeJS.Timeout | undefined;
  realtimeTimer: NodeJS.Timeout | undefined;
  exceptionMap: Map<number, string>;
  constructor (url: ()=>string) {
    super();
    this.url = url;
    this.retryTimes = 0;
    this.subjectMap = new Map();
    this.dataStore = new Map();
    this.priceDataStore = new Map();
    this.realtimeSymbolStore = new Map();
    this.exceptionMap = new Map();
    this.klineDataStore = [];
    this.latestSymbol = 0;
    this.isApplicationActive = document.visibilityState === 'visible';
    // this.connect();
  }

  close (code?: number, reason?: string) {
    this.ws?.close(code, reason);
  }

  manualClose() {
    if (this.heartBeat) {
      clearTimeout(this.heartBeat);
      this.heartBeat = undefined;
    }
    if (this.connectTimer) {
      clearTimeout(this.connectTimer);
      this.connectTimer = undefined;
    }
    document.removeEventListener('visibilitychange', this.handleActiveChange, false);
    this.ws?.close(1000, 'manual close');
  }

  connect () {
    // 先清除之前注册的监听器，避免重复注册
    document.removeEventListener('visibilitychange', this.handleActiveChange, false);
    document.addEventListener('visibilitychange', this.handleActiveChange, false);
    const url = this.url()
    this.ws = new WebSocket(this.url())
    setTimeout(()=>{
      if (url!=this.url()) {
        this.reconnect()
      }
    },3000)
    this.ws.binaryType = 'arraybuffer';
    if (this.heartBeat) {
      clearTimeout(this.heartBeat);
      this.heartBeat = undefined;
    }
    this.ws.onclose = (event) => {
      if (event.code === 1000) {
        return;
      }
      this.reconnect();
    };
    this.ws.onerror = (event) => {
      this.close();
    };
    this.ws.onopen = () => {
      this.retryTimes = 0;
      for (const [subTitle, subItems] of this.subjectMap) {
        subItems.forEach((subData) => {
          this.send({
            subscribe: subTitle,
            ...subData,
          });
        });
      }
    };
    this.ws.onmessage = (event) => {
      this.initHeartBeat();
      if (event.data) {
        try {
          let wsres;
          if (event.data.search && event.data.replace) {
            try {
              wsres = JSON.parse(event.data);
            } catch (e) {
              // 除了pong，其他的数据上报sentry异常
              if (event.data !== 'pong') {
                Sentry.captureMessage('trade_ws_response_not_json', {
                  level: 'warning',
                  extra: {
                    info: JSON.stringify(event),
                  },
                });
              }
              return;
            }
          } else {
            wsres = pako.inflateRaw(event.data, {
              to: 'string',
            });
            try {
              wsres = JSON.parse(wsres);
            } catch (e) {
              // 除了pong，其他的数据上报sentry异常
              if (event.data !== 'pong') {
                Sentry.captureMessage('trade_ws_response_not_json', {
                  level: 'warning',
                  extra: {
                    info: JSON.stringify(event),
                    wsres,
                  },
                });
              }
            }
          }
          // 以下是subscribe类型的异常code处理
          if (!wsres.request && wsres.code !== undefined && +wsres.code !== 0) {
            // 8102 是重复订阅异常，不需要统计
            if (!this.exceptionMap.has(wsres.code) && wsres.code !== -8102) {
              // 只有第一次出现异常的时候，才需要上报
              const msg = wsres.msg || wsres.errMsg;
              if (SENTRY_ENABLE) {
                Sentry.captureMessage('spot_ws_exception', {
                  level: 'warning',
                  tags: {
                    code: wsres.code,
                  },
                  contexts: {
                    msg,
                  },
                });
              }
              this.exceptionMap.set(wsres.code, msg);
              if (wsres.code === -8107) {
                debounceMessage({
                  message: msg,
                  level: 'warning',
                });
              }
            }
            if (wsres.code === -8107) {
              // 限频
              this.retryTimes = 3;
            }
            return;
          }
          // request类型的错误码处理方式都不一样，所以不做统一处理
          this.dispatchData(wsres);
          wsres = null;
        } catch (err) {
          SENTRY_ENABLE && Sentry.captureException(err);
        }
      }
    };
    this.requestScheduleMessage();
    this.requestRealtimeMessage();
  }

  handleActiveChange = () => {
    this.isApplicationActive = document.visibilityState === 'visible';
    if (this.scheduleTimer) {
      clearTimeout(this.scheduleTimer);
      this.scheduleTimer = undefined;
    }
    if (this.realtimeTimer) {
      clearTimeout(this.realtimeTimer);
      this.realtimeTimer = undefined;
    }
    this.requestScheduleMessage();
    this.requestRealtimeMessage();
  };

  dispatchData (wsData: WsData) {
    // request类型是单次订阅并且可能需要实时结果响应，所以不做缓存
    if (wsData.request) {
      this.emit('postMessage', {
        type: wsData.request,
        payload: wsData,
      });
      return;
    }
    const tradeStore = useTradeStore();
    switch (wsData.subscribe) {
      case NORMAL_SUBSCRIBE_MAP.PRICE:
        if (tradeStore.symbolInfo?.symbolId && +tradeStore.symbolInfo?.symbolId === wsData.symbol) {
          this.realtimeSymbolStore.set(wsData.symbol, wsData);
          if (!this.isApplicationActive) {
            this.requestRealtimeMessage();
          }
        } else {
          this.priceDataStore.set(wsData.symbol, wsData);
        }
        break;
      case NORMAL_SUBSCRIBE_MAP.TRADE:
        // if (this.dataStore.has(wsData.subscribe)) {
        //   (this.dataStore.get(wsData.subscribe) as WsData[]).push(wsData);
        // } else {
        //   this.dataStore.set(wsData.subscribe, [wsData]);
        // }
        if (this.isApplicationActive) {
          this.emit('postMessage', {
            type: wsData.subscribe,
            payload: wsData,
          });
        }
        break;
      case NORMAL_SUBSCRIBE_MAP.DEPTH:
        this.dataStore.set(wsData.subscribe, wsData);
        break;
      case NORMAL_SUBSCRIBE_MAP.KLINE:
        this.klineDataStore.push(wsData);
        // 避免 setTimeout 被节流的情况下OOM
        if (this.klineDataStore.length >= 1) {
          this.emitKlineData();
        }
        break;
      default:
        break;
    }
    // TODO - $serverTimeDiff暂时未注入，代码临时注释
    // if (wsData.uuid && wsData.timeStamp) {
    //   const now = Date.now();
    //   const delay = now - wsData.timeStamp - Math.max(window.$nuxt.$serverTimeDiff, 0);
    //   if (delay > WsSubject.WS_DELAY_THRESHOLD) {
    //     this.delayReport({
    //       uuid: wsData.uuid,
    //       delay,
    //       serverTimeDiff: window.$nuxt.$serverTimeDiff,
    //     });
    //   }
    // }
  }

  // 节流 15s 一次
  delayReport = throttle((extra: {
    [x: string]: unknown;
  }) => {
    Sentry.captureMessage('spot_ws_delay_event', {
      level: 'warning',
      extra,
    });
  }, 15 * 1e3);

  /**
   * 为什么要单独抽一个方法来消费 kline data
   * 因为 klineDataStore 是数组，如果定时器被截留了，数组一直push 会导致OOM
   */
  emitKlineData () {
    // console.log('emitKlineData', this.isApplicationActive, {
    //   klineDataStore: this.klineDataStore.length,
    // })
    while (this.klineDataStore.length) {
      const klineData = this.klineDataStore.shift();
      this.emit('postMessage', {
        type: NORMAL_SUBSCRIBE_MAP.KLINE,
        payload: klineData,
      });
    }
  }

  requestScheduleMessage () {
    // console.log('requestScheduleMessage', this.isApplicationActive, {
    //   priceDataStore: this.priceDataStore.size,
    //   klineDataStore: this.klineDataStore.length,
    // })
    this.emitKlineData();
    for (const [subscribe, data] of this.dataStore) {
      this.emit('postMessage', {
        type: subscribe,
        payload: data,
      });
      this.dataStore.delete(subscribe);
    }
    for (const [symbol, data] of this.priceDataStore) {
      this.emit('postMessage', {
        type: NORMAL_SUBSCRIBE_MAP.PRICE,
        payload: data,
      });
      this.priceDataStore.delete(symbol);
    }
    this.scheduleTimer && clearTimeout(this.scheduleTimer);
    this.scheduleTimer = setTimeout(
      () => {
        this.requestScheduleMessage();
      },
      this.isApplicationActive ? 500 : 5000,
    );
  }

  requestRealtimeMessage () {
    // console.log('requestRealtimeMessage', this.isApplicationActive, {
    //   realtimeSymbolStore: this.realtimeSymbolStore.size,
    // })
    for (const [symbol, data] of this.realtimeSymbolStore) {
      this.emit('postMessage', {
        type: NORMAL_SUBSCRIBE_MAP.PRICE,
        payload: data,
      });
      this.realtimeSymbolStore.delete(symbol);
    }
    this.realtimeTimer && clearTimeout(this.realtimeTimer);
    this.realtimeTimer = setTimeout(
      () => {
        this.requestRealtimeMessage();
      },
      this.isApplicationActive ? 500 : 1000,
    );
  }

  reconnect () {
    const timeout = Math.pow(2, this.retryTimes);
    if (this.retryTimes <= 3) {
      this.retryTimes += 1;
    }
    // console.log('timeout', timeout);
    if (this.connectTimer) {
      clearTimeout(this.connectTimer);
    }
    this.connectTimer = setTimeout(() => {
      this.connect();
    }, timeout * 1e3);
  }

  initHeartBeat () {
    if (this.heartBeat) {
      clearTimeout(this.heartBeat);
    }
    this.heartBeat = setTimeout(() => {
      if (this.ws?.readyState === 1) {
        this.ws?.send('ping');
      }
    }, 20000);
  }

  send (subject) {
    if (this.ws?.readyState === 1) {
      this.ws?.send(JSON.stringify(subject));
    }
  }

  /**
   * 为减少使用者身心负担，应判断是否自动为其取消之前的订阅
   * 无论怎么走，都会走到最终的send
   * @param {*} subTitle
   * @param {*} subData
   * @param {boolean} uniqSubscribe 默认true，即默认只订阅一种Key
   */
  subscribe (subTitle: string, subData: object, uniqSubscribe = true) {
    if (this.subjectMap.has(subTitle)) {
      const subItems = this.subjectMap.get(subTitle) as object[];
      const hasAllEqual = subItems.some(item => isEqual(item, subData));
      if (!hasAllEqual) {
        // key 已经在订阅列表中，但是是订阅参数不同
        // 走 uniqSubscribe 逻辑
        if (uniqSubscribe) {
          // 只订阅一种key，只需要传key值，因为是不同订阅参数
          // 针对 subjectMap 操作会在 unsubscribe 完成
          this.unsubscribe(subTitle);
          this.subjectMap.set(subTitle, [subData]);
        } else {
          // 订阅多个key
          subItems.push(subData);
          this.subjectMap.set(subTitle, subItems);
        }
      } else {
        // do nothing, 再发一次订阅没关系
      }
    } else {
      this.subjectMap.set(subTitle, [subData]);
    }

    this.send({
      subscribe: subTitle,
      ...subData,
    });
  }

  /**
   * 取消订阅，为避免无效取消，引起并发错误，应该做一些健壮
   * 如果中途 return ，则不会走到最终send
   * @param {*} subTitle
   * @param {*} subData
   * @param {boolean} skipEqualCheck 默认false，即默认需要判断是否有相同的订阅参数, 为true时，跳过判断，直接发送
   */
  unsubscribe (subTitle: string, subData?: object, skipEqualCheck = false) {
    // 为避免并发订阅错误，do nothing
    if (!this.subjectMap.has(subTitle)) {
      return;
    }

    const subItems = this.subjectMap.get(subTitle) as object[];
    if (subData) {
      const matchIndex = subItems.findIndex(item => isEqual(item, subData));
      const isAllEqual = matchIndex > -1;
      // 有key，但是没有参数相同的，可以认为是无效的取消订阅
      // 为避免并发订阅错误，do nothing
      if (!skipEqualCheck && !isAllEqual) {
        return;
      }

      this.send({
        subscribe: `${subTitle}_cancel`,
        ...subData,
      });
      subItems.splice(matchIndex, 1);
    } else {
      // 全部取消订阅
      subItems.forEach((subItem) => {
        if (subItem.event === 'addChannel') {
          // 特殊逻辑
          subItem.event = 'cancelChannel';
        }
        this.send({
          subscribe: `${subTitle}_cancel`,
          ...subItem,
        });
      });
      this.subjectMap.set(subTitle, []);
      // this.subjectMap.delete(subTitle)
    }
  }

  /** request类型订阅
   * @param {*} subTitle request的类型，例如tickers
   * @param {*} subData request的参数，例如{ type: 'auto' }
   */
  request (subTitle: string, subData: object) {
    this.send({
      request: subTitle,
      ...subData,
    });
  }

  /** 校验交易对，如果当前交易对跟订阅的交易对不一致，向sentry上报异常 */
  checkSymbol (symbol: number, subscribe: string) {
    const tradeStore = useTradeStore();
    if (+tradeStore.symbolInfo?.symbolId !== symbol && this.latestSymbol !== symbol) {
      Sentry.captureMessage('spot_ws_other_symbol_quotes', {
        level: 'warning',
        extra: {
          fetchSymbol: symbol,
          currentSymbol: tradeStore.symbolInfo?.symbolId,
          subscribe,
        },
      });
    } else {
      this.latestSymbol = symbol;
    }
  }
}

export const ws = new WsSubject(getWsUrl);

ws.on('message', (msg) => {
  const payload: object = JSON.parse(JSON.stringify(msg.payload));
  switch (msg.type) {
    case WORKER_MSG_TOPIC.get('SUBSCRIBE'):
      if (!ws.ws || ws.ws.readyState === WebSocket.CLOSED || ws.ws.readyState === WebSocket.CLOSING) {
        ws.connect();
      }
      ws.subscribe(...payload);
      break;
    case WORKER_MSG_TOPIC.get('UNSUBSCRIBE'):
      if (!ws.ws) {
        ws.connect();
      }
      ws.unsubscribe(...payload);
      break;
    case WORKER_MSG_TOPIC.get('REQUEST'):
      if (!ws.ws || ws.ws.readyState === WebSocket.CLOSED || ws.ws.readyState === WebSocket.CLOSING) {
        ws.connect();
      }
      ws.request(...payload);
      break;
  }
});
